import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentLedgerWithInstallments } from './api/studentScreensApi'

const PAGE_SIZES = [10, 25, 50, 100]

function KPICard({ label, value, color }) {
  const colorMap = {
    blue: 'bg-blue-500 text-white',
    green: 'bg-green-500 text-white',
    purple: 'bg-purple-500 text-white',
    red: 'bg-red-500 text-white',
  }
  return (
    <div className={`flex flex-col items-center justify-center rounded-2xl px-4 py-5 ${colorMap[color]}`}>
      <span className="text-xl font-black">{value}</span>
      <span className="mt-1.5 text-[11px] font-bold uppercase tracking-wider opacity-90">{label}</span>
    </div>
  )
}

function fmt(n) {
  return n.toLocaleString('en-IN', { minimumFractionDigits: 2 })
}

export default function StudentLedgerInstallmentsScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pageSize, setPageSize] = useState(25)
  const [entries, setEntries] = useState([])
  const [totals, setTotals] = useState({ debit: 0, credit: 0, balance: 0, overdue: 0 })

  useEffect(() => { loadStudent() }, [studentId])
  useEffect(() => { loadLedger() }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadLedger = async () => {
    try {
      const data = await getStudentLedgerWithInstallments(studentId)
      const rows = (data?.rows || []).map((r) => ({
        ...r,
        particulars: r.acHead || r.no || '',
        no: r.ref || r.no || '',
        ref: r.ref || '',
      }))
      setEntries(rows)
      setTotals({
        debit: data?.totalDebit || 0,
        credit: data?.totalCredit || 0,
        balance: data?.balance || 0,
        overdue: data?.overdue || 0,
      })
    } catch {
      setEntries([])
      setTotals({ debit: 0, credit: 0, balance: 0, overdue: 0 })
    }
  }

  const pagedEntries = useMemo(() => entries.slice(0, pageSize), [entries, pageSize])

  const goBack = () => navigate('/admission/students/list')
  const goPrint = () => window.print()
  const name = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'
  const sid = student?.studentCode || student?.id || '—'

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Ledger With Installments</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">Ledger With Installments — {name}#{sid}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={goPrint} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Print">
            <span className="material-symbols-outlined text-[20px]">print</span>
          </button>
          <button type="button" onClick={goBack} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Back">
            <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          </button>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <KPICard label="Debits" value={`₹${fmt(totals.debit)}`} color="blue" />
        <KPICard label="Credits" value={`₹${fmt(totals.credit)}`} color="green" />
        <KPICard label="Balance" value={`₹${fmt(totals.balance)}`} color="purple" />
        <KPICard label="Over Due" value={`₹${fmt(totals.overdue)}`} color="red" />
      </div>

      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-on-primary text-[22px]">payments</span>
          <h2 className="text-base font-bold text-on-primary">Ledger With Installments — {name}#{sid}</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-on-primary/80">Show</span>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-lg border border-white/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-white/40">
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs font-bold text-on-primary/80">entries</span>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/30 bg-surface-container-low">
              <th className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Date</th>
              <th className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant">Particulars</th>
              <th className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant text-right">Debit</th>
              <th className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant text-right">Credit</th>
              <th className="px-4 py-3.5 text-xs font-black uppercase tracking-wider text-on-surface-variant text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                  <p className="mt-3 text-sm text-on-surface-variant">Loading ledger...</p>
                </td>
              </tr>
            ) : pagedEntries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-12">
                  <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                    <p className="text-center text-sm font-bold leading-relaxed text-blue-600">
                      No entries meet selected criteria — Either there are no entries in the selected module or you have selected invalid filter conditions.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {pagedEntries.map((entry, idx) => (
                  <tr key={entry.id} className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}>
                    <td className="px-4 py-3 text-sm text-on-surface font-mono">{entry.date}</td>
                    <td className="px-4 py-3 text-sm text-on-surface">{entry.particulars}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface font-mono">{entry.debit > 0 ? fmt(entry.debit) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-on-surface font-mono">{entry.credit > 0 ? fmt(entry.credit) : '—'}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-on-surface font-mono">{fmt(entry.balance)} {entry.balanceSide}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-outline-variant/30 bg-surface-container-low font-bold">
                  <td colSpan={2} className="px-4 py-3.5 text-sm text-on-surface">Closing Balance</td>
                  <td className="px-4 py-3.5 text-sm text-right font-mono text-on-surface">{totals.debit > 0 ? fmt(totals.debit) : '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-mono text-on-surface">{totals.credit > 0 ? fmt(totals.credit) : '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-right font-black text-primary font-mono">{fmt(totals.balance)} Dr</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on <span className="font-semibold text-on-surface">{student.createdDate}</span></span>
          )}
          {student?.updatedBy && (
            <span>Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on <span className="font-semibold text-on-surface">{student.updatedDate}</span></span>
          )}
        </div>
      </section>
    </div>
  )
}
